import { Controller } from "stimulus"
import { disableSubmitButtons } from "../form_submission"
import { initializeTinyMce, removeTinyMce } from "../tinymce"

export default class extends Controller {
  static targets = ["form", "status", "errors", "searchControl", "search", "results", "questions", "question", "template", "empty", "sectionCount"]

  connect() {
    this.dirty = this.element.dataset.templateEditorInvalid === "true"
    this.busy = false
    this.sequence = 0
    this.navigate = this.navigate.bind(this)
    this.beforeUnload = this.beforeUnload.bind(this)
    document.addEventListener("click", this.navigate, true)
    window.addEventListener("beforeunload", this.beforeUnload)
    if (!this.hasFormTarget) return
    this.searchControlTarget.hidden = false
    this.questionTargets.forEach(question => this.updateQuestion(question))
    this.filter()
    initializeTinyMce(this.element)
    if (this.hasErrorsTarget) this.errorsTarget.focus()
  }

  disconnect() {
    document.removeEventListener("click", this.navigate, true)
    window.removeEventListener("beforeunload", this.beforeUnload)
    removeTinyMce(this.element)
  }

  richTextOpened(event) {
    if (event.currentTarget.open) initializeTinyMce(this.element)
  }

  changed(event) {
    if (event.target === this.searchTarget) return
    const question = event.target.closest(".rt-question")
    if (question) this.updateQuestion(question)
    this.markDirty()
  }

  markDirty() {
    this.dirty = true
    this.statusTarget.textContent = "Nesaglabātas izmaiņas"
  }

  typeChanged(event) {
    this.updateQuestion(event.target.closest(".rt-question"))
  }

  updateQuestion(question) {
    if (this.removed(question)) question.querySelector('[data-role="question"]').required = false
    const title = question.querySelector('[data-role="question"]').value
    const type = question.querySelector('[data-role="type"]')
    const required = question.querySelector('[data-role="required"]').checked
    question.querySelector('[data-role="title"]').textContent = title || "Jauns jautājums"
    question.querySelector('[data-role="meta"]').textContent = `${type.selectedOptions[0].textContent} · ${required ? "Obligāts" : "Nav obligāts"} · Labot`
    question.querySelector('[data-role="assessment"]').hidden = type.value !== "self_assessment_field"
  }

  searchKey(event) {
    if (event.key === "Enter") event.preventDefault()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase("lv")
    let visible = 0
    let total = 0
    this.questionTargets.forEach(question => {
      if (!this.removed(question)) total += 1
      const content = Array.from(question.querySelectorAll("textarea, select")).map(input => input.value).join(" ").toLocaleLowerCase("lv")
      const matches = !query || content.includes(query) || question.dataset.invalid === "true"
      question.hidden = !matches
      if (matches && !this.removed(question)) visible += 1
    })
    this.emptyTarget.hidden = total > 0
    this.resultsTarget.textContent = query ? `Atrasti ${visible} no ${total} jautājumiem.` : `${total} jautājumi šajā sadaļā.`
    if (this.hasSectionCountTarget) this.sectionCountTarget.textContent = total
  }

  removed(question) {
    return question.querySelector('input[name$="[_destroy]"]').value === "1"
  }

  add() {
    this.searchTarget.value = ""
    const id = `${Date.now()}${String(this.sequence++).padStart(4, "0")}`
    this.questionsTarget.insertAdjacentHTML("beforeend", this.templateTarget.innerHTML.replace(/NEW_FIELD/g, id))
    const question = this.questionsTarget.lastElementChild
    this.updateQuestion(question)
    this.filter()
    question.querySelector("details").open = true
    question.querySelector('[data-role="question"]').focus()
    this.markDirty()
  }

  remove(event) {
    if (!window.confirm("Jautājums tiks noņemts pēc saglabāšanas. Turpināt?")) return
    const question = event.currentTarget.closest(".rt-question")
    question.querySelector('input[name$="[_destroy]"]').value = "1"
    question.querySelector("details").hidden = true
    question.querySelector(".rt-removed").hidden = false
    question.querySelector('[data-role="question"]').required = false
    this.markDirty()
    this.filter()
  }

  restore(event) {
    const question = event.currentTarget.closest(".rt-question")
    question.querySelector('input[name$="[_destroy]"]').value = "0"
    question.querySelector("details").hidden = false
    question.querySelector("details").open = true
    question.querySelector(".rt-removed").hidden = true
    const input = question.querySelector('[data-role="question"]')
    input.required = input.dataset.questionRequired === "true"
    this.markDirty()
    this.filter()
  }

  collapse(event) {
    const details = event.currentTarget.closest("details")
    details.open = false
    details.querySelector("summary").focus()
  }

  navigate(event) {
    const link = event.target.closest("a[href]")
    if (!link || link.target === "_blank" || link.getAttribute("href").startsWith("#") || event.button !== 0 ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
    if (this.busy || (this.dirty && !window.confirm("Ir nesaglabātas izmaiņas. Atstāt lapu bez saglabāšanas?"))) {
      event.preventDefault()
      event.stopImmediatePropagation()
    } else this.dirty = false
  }

  beforeUnload(event) {
    if (!this.dirty && !this.busy) return
    event.preventDefault()
    event.returnValue = ""
  }

  review(event) {
    if (!this.dirty && !this.busy) return
    event.preventDefault()
    event.stopImmediatePropagation()
    window.alert('Vispirms saglabājiet izmaiņas, pēc tam atzīmējiet sadaļu kā pārskatītu.')
  }

  async submit(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.busy) return
    const form = this.formTarget
    if (!form.checkValidity()) {
      this.searchTarget.value = ""
      this.filter()
      form.querySelectorAll(":invalid").forEach(input => {
        const details = input.closest("details")
        if (details) details.open = true
      })
      form.reportValidity()
      return
    }
    if (window.tinymce) window.tinymce.triggerSave()
    const body = new FormData(form)
    const open = this.questionTargets.filter(q => q.querySelector("details").open).map(q => ({ id: q.dataset.fieldId, title: q.querySelector('[data-role="question"]').value }))
    const search = this.searchTarget.value
    const restore = disableSubmitButtons(form)
    this.busy = true
    form.setAttribute("aria-busy", "true")
    form.setAttribute("inert", "")
    this.statusTarget.textContent = "Saglabā…"
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')
      const response = await fetch(form.action, {
        method: "POST", body, credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf ? csrf.content : "" }
      })
      if (response.redirected || (!response.ok && response.status !== 422)) throw new Error("Save failed")
      const data = await response.json()
      const fragment = new DOMParser().parseFromString(data.html, "text/html")
      const incoming = fragment.querySelector(".report-template-management")
      if (!incoming) throw new Error("Missing editor")
      if (!response.ok && !incoming.querySelector('form')) {
        this.dirty = true
        this.statusTarget.textContent = "Sagatave vairs nav melnraksts. Labojumi nav saglabāti; ievadītās vērtības paliek šajā lapā."
        return
      }
      const input = incoming.querySelector('#template-question-search')
      if (input) input.value = search
      incoming.querySelectorAll(".rt-question").forEach(question => {
        const title = question.querySelector('[data-role="question"]').value
        if (open.some(q => (q.id && q.id === question.dataset.fieldId) || (!q.id && q.title === title))) question.querySelector("details").open = true
      })
      this.dirty = !response.ok
      removeTinyMce(this.element)
      this.element.replaceWith(incoming)
    } catch (_error) {
      this.dirty = true
      this.statusTarget.textContent = "Neizdevās apstiprināt saglabāšanu. Pārbaudi savienojumu un mēģini vēlreiz."
    } finally {
      this.busy = false
      form.removeAttribute("aria-busy")
      form.removeAttribute("inert")
      restore()
    }
  }
}
